import React, { useState } from 'react';
import { Menu, X, Phone, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { SWIGGY_ORDER_URL, ZOMATO_ORDER_URL } from '../data/mockData';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsOpen(false);
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer" onClick={() => scrollToSection('home')}>
            <img
              src="/ambarsariya-logo.png"
              alt="Ambarsariya - The Taste of Amritsar"
              className="h-14 md:h-16 w-auto object-contain"
              data-testid="navbar-logo"
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('home')}
              className="text-gray-700 hover:text-yellow-600 font-medium transition-colors"
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-yellow-600 font-medium transition-colors"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('menu')}
              className="text-gray-700 hover:text-yellow-600 font-medium transition-colors"
            >
              Menu
            </button>
            <button
              onClick={() => scrollToSection('gallery')}
              className="text-gray-700 hover:text-yellow-600 font-medium transition-colors"
            >
              Gallery
            </button>
            <button
              onClick={() => scrollToSection('reviews')}
              className="text-gray-700 hover:text-yellow-600 font-medium transition-colors"
            >
              Reviews
            </button>
            <button
              onClick={() => scrollToSection('news')}
              className="text-gray-700 hover:text-yellow-600 font-medium transition-colors"
            >
              News
            </button>
            <button
              onClick={() => scrollToSection('franchise')}
              className="text-gray-700 hover:text-yellow-600 font-medium transition-colors"
            >
              Franchise
            </button>
            <Button
              asChild
              data-testid="navbar-order-now-btn"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold"
            >
              <a href={SWIGGY_ORDER_URL} target="_blank" rel="noopener noreferrer">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Swiggy
              </a>
            </Button>
            <Button
              asChild
              data-testid="navbar-order-zomato-btn"
              className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-semibold"
            >
              <a href={ZOMATO_ORDER_URL} target="_blank" rel="noopener noreferrer">
                <ShoppingBag className="w-4 h-4 mr-2" />
                Zomato
              </a>
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-800 font-semibold"
            >
              <Phone className="w-4 h-4 mr-2" />
              Contact
            </Button>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 hover:text-yellow-600 transition-colors"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="md:hidden pb-4">
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => scrollToSection('home')}
                className="text-gray-700 hover:text-yellow-600 font-medium transition-colors py-2"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-gray-700 hover:text-yellow-600 font-medium transition-colors py-2"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('menu')}
                className="text-gray-700 hover:text-yellow-600 font-medium transition-colors py-2"
              >
                Menu
              </button>
              <button
                onClick={() => scrollToSection('gallery')}
                className="text-gray-700 hover:text-yellow-600 font-medium transition-colors py-2"
              >
                Gallery
              </button>
              <button
                onClick={() => scrollToSection('reviews')}
                className="text-gray-700 hover:text-yellow-600 font-medium transition-colors py-2"
              >
                Reviews
              </button>
              <button
                onClick={() => scrollToSection('news')}
                className="text-gray-700 hover:text-yellow-600 font-medium transition-colors py-2"
              >
                News
              </button>
              <button
                onClick={() => scrollToSection('franchise')}
                className="text-gray-700 hover:text-yellow-600 font-medium transition-colors py-2"
              >
                Franchise
              </button>
              <Button
                asChild
                data-testid="navbar-mobile-order-now-btn"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold w-full"
              >
                <a href={SWIGGY_ORDER_URL} target="_blank" rel="noopener noreferrer">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Order on Swiggy
                </a>
              </Button>
              <Button
                asChild
                data-testid="navbar-mobile-order-zomato-btn"
                className="bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 text-white font-semibold w-full"
              >
                <a href={ZOMATO_ORDER_URL} target="_blank" rel="noopener noreferrer">
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Order on Zomato
                </a>
              </Button>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-800 font-semibold w-full"
              >
                <Phone className="w-4 h-4 mr-2" />
                Contact
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
